import { addDays } from 'date-fns';

/** PDGM pays in fixed 30-day periods (matches backend episode_lupa.py). */
const PDGM_PERIOD_DAYS = 30;

export type PdgmPeriodBoundaries = {
  periodCount: 1 | 2;
  period1End: Date;
  period2Start: Date | null;
};

const calendarDaySpan = (startDate: Date, endDate: Date): number =>
  Math.round((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));

const isSameCalendarDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Number of PDGM 30-day periods an episode spans.
 * Mirrors backend ``pdgm_period_count``.
 */
export const getPdgmPeriodCount = (startDate: Date, endDate: Date): 1 | 2 =>
  calendarDaySpan(startDate, endDate) < PDGM_PERIOD_DAYS ? 1 : 2;

/**
 * Resolve period-1 end and period-2 start for calendar boundary styling.
 * Returns null when dates are invalid or end precedes start.
 */
export const getPdgmPeriodBoundaries = (
  startDate: Date,
  endDate: Date
): PdgmPeriodBoundaries | null => {
  if (endDate < startDate) {
    return null;
  }

  const periodCount = getPdgmPeriodCount(startDate, endDate);
  if (periodCount === 1) {
    return {
      periodCount: 1,
      period1End: endDate,
      period2Start: null,
    };
  }

  const period2Start = addDays(startDate, PDGM_PERIOD_DAYS);
  const period1End = addDays(period2Start, -1);
  return {
    periodCount: 2,
    period1End,
    period2Start,
  };
};

/** Whether a calendar day falls in the given PDGM period (1 or 2). */
export const isInPdgmPeriod = (
  date: Date,
  periodNumber: 1 | 2,
  boundaries: PdgmPeriodBoundaries,
  episodeStart: Date,
  episodeEnd: Date
): boolean => {
  if (date < episodeStart || date > episodeEnd) {
    return false;
  }
  if (boundaries.periodCount === 1) {
    return periodNumber === 1;
  }
  if (periodNumber === 1) {
    return date <= boundaries.period1End;
  }
  return boundaries.period2Start != null && date >= boundaries.period2Start;
};

export const isPdgmPeriodBoundaryEnd = (
  date: Date,
  boundaries: PdgmPeriodBoundaries
): boolean =>
  boundaries.periodCount === 2 &&
  isSameCalendarDay(date, boundaries.period1End);

export const isPdgmPeriodBoundaryStart = (
  date: Date,
  boundaries: PdgmPeriodBoundaries
): boolean =>
  boundaries.periodCount === 2 &&
  boundaries.period2Start != null &&
  isSameCalendarDay(date, boundaries.period2Start);
